import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Rect,
  Defs,
  LinearGradient,
  Stop,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import { ViolationNote } from '../types';

const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 50,
    fontFamily: 'Helvetica',
    color: '#333',
  },
  topSection: {
    display: 'flex',
    padding: 40,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 32,
    alignSelf: 'stretch',
  },
  bottomSection: {
    display: 'flex',
    paddingHorizontal: 40,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 23,
    alignSelf: 'stretch',
  },
  section: {
    marginBottom: 20,
  },
  label: {
    fontSize: 10,
    color: '#999999',
    marginBottom: 6,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  image: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 10,
  },
  displayName: {
    fontSize: 13,
    // fontWeight: 'bold',
    color: '#333333',
  },
  email: {
    fontSize: 12,
    color: '#555555',
    marginTop: 2,
  },
  designation: {
    fontSize: 11,
    color: '#999999',
    marginTop: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
    width: '100%',
  },
  column: {
    flexDirection: 'column',
    width: '48%',
  },
  htmlText: {
    fontSize: 12,
    color: '#444444',
    lineHeight: 1.6,
  },
  smallText: {
    fontSize: 12,
    color: '#333333',
    marginTop: 4,
  },
});

export const PdfGeneration = ({ violationNote }: { violationNote: ViolationNote }) => {
  const { raisedBy, raisedFor, description, reportedTime, type } = violationNote ?? {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Top Section */}
        <View style={styles.topSection}>
          <View style={styles.section}>
            <Text style={styles.label}>Reported employee:</Text>
            <View style={styles.nameRow}>
              <Image style={styles.image} src={raisedFor?.profilePhoto} />
              <View style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={styles.displayName}>{raisedFor?.displayName}</Text>
                <Text style={styles.email}>{calculateDisplayEmail(raisedFor)}</Text>
                <Text style={styles.designation}>{raisedFor?.designation}</Text>
              </View>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.label}>Reported by:</Text>
            <View style={styles.nameRow}>
              <Image style={styles.image} src={raisedBy?.profilePhoto} />
              <View style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={styles.displayName}>{raisedBy?.displayName}</Text>
                <Text style={styles.email}>{calculateDisplayEmail(raisedBy)}</Text>
                <Text style={styles.designation}>{raisedBy?.designation}</Text>
              </View>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Date & time:</Text>
              <Text style={styles.email}>{format(new Date(reportedTime), 'PPP • p')}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Type</Text>
              <Text style={styles.email}>{type}</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.label}>Description:</Text>
            <Text style={styles.htmlText}>{description}</Text>
          </View>
        </View>

        {/* Sign Section */}
        <View style={styles.bottomSection}>
          <View style={styles.section}>
            <Text style={styles.label}>Acknowledgement:</Text>
            <View style={{ display: 'flex', flexDirection: 'column' }}>
              {/* <Text style={{ color: 'transparent' }}>{`{t:s;r:y;o:"signer";w:100;h:15;}`}</Text> */}
              <Text style={{ color: 'transparent' }}>{'{{signature}}'}</Text>
              <Text style={styles.smallText}>{raisedFor?.displayName}</Text>
              <Text style={{ color: 'transparent' }}>{'{{sign_date}}'}</Text>
            </View>
          </View>
        </View>

        <View
          fixed
          style={{
            borderTop: '1 solid #EAEAEA',
            position: 'absolute',
            bottom: 20,
            width: 595, // A4 width in points minus margins
            minHeight: 30,
          }}
          // @ts-expect-error wrong type, check github; totalPages exists
          render={({ pageNumber, totalPages }) => {
            if (pageNumber !== totalPages) {
              return (
                <View>
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      height: '30',
                      paddingLeft: 24,
                      paddingRight: 32,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: 'transparent' }}>{'{{signature}}'}</Text>

                    {/* --------------------  Name  -------------------------------- */}

                    <Text style={styles.smallText}>Signed by: {raisedFor?.displayName}</Text>

                    {/* ----------------------  date  ------------------------------ */}

                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.smallText}>Signed on: </Text>
                      <Text
                        style={{
                          color: 'transparent',
                        }}
                      >
                        {'{{sign_date}}'}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            }
            return null;
          }}
        />
        {/* Footer - Page Number */}
        <View
          fixed
          style={{
            position: 'absolute',
            bottom: 10,
            right: 40,
          }}
          render={({ pageNumber }) => (
            <Text
              style={{
                color: '#333333',
                fontSize: 12,
              }}
            >
              {pageNumber > 9 ? pageNumber : `0${pageNumber}`}
            </Text>
          )}
        />

        {/* Footer - Gradient Bar */}
        <View
          fixed
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          {/* 595 -> width of A4 page size */}
          <Svg width="595" height="4">
            <Defs>
              <LinearGradient id="paint" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
                <Stop stopColor="#008EF0" offset="0" />
                <Stop offset="0.336458" stopColor="#EC3B64" />
                <Stop offset="0.648958" stopColor="#FFC200" />
                <Stop offset="1" stopColor="#5CC461" />
              </LinearGradient>
            </Defs>
            <Rect width="595" height="4" fill="url(#paint)" />
          </Svg>
        </View>
      </Page>
    </Document>
  );
};
